<template>
  <v-dialog
    data-component="ModalComponent"
    class="modal-component"
    v-model="dialog"
    persistent
    @click="closeModalByClickingOut"
  >
    <section
      class="modal-panel"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="title ? 'modal-title' : undefined"
    >
      <div class="header">
        <div
          class="title"
          id="modal-title"
        >
          <slot name="title" />
        </div>
        <button
          v-if="!hideClose"
          type="button"
          class="close-btn"
          :aria-label="ARIA_LABELS.CLOSE"
          @click="closeDialog"
        >
          <i
            class="icon-close-03"
            aria-hidden="true"
          />
        </button>
      </div>

      <div class="body">
        <ToasterComponent
          :toasts="toastStore.getModalToasts()"
          @close="toastStore.removeToast"
          @pause="toastStore.pauseToast"
          @resume="toastStore.resumeToast"
        />

        <slot />
      </div>
    </section>
  </v-dialog>
</template>

<script lang="ts" setup>
  import ToasterComponent from '@/modules/core/components/messages/ToasterComponent.vue'
  import { ARIA_LABELS } from '@/modules/core/constants/ariaLabels'
  import { useMessageStore } from '@/modules/core/stores/messageStore'
  import { useToastStore } from '@/modules/core/stores/toastStore'
  import { onBeforeUnmount, ref, watch } from 'vue'

  const toastStore = useToastStore()
  const messageStore = useMessageStore()

  const props = defineProps({
    title: String,
    value: Boolean, // Añadimos esta prop para controlar la visibilidad desde el padre
    type: String,
    hideClose: Boolean,
    exitClickOut: Boolean //Si es a true permite cerrar modales cuando pulsamos fuera, sino no.
  })

  const dialog = ref(props.value)

  // Define un evento para emitir la validez del email al componente padre.
  const emit = defineEmits(['update:value'])

  watch(dialog, (newVal) => {
    emit('update:value', newVal)
  })

  // Asegúrate de actualizar 'dialog' cuando 'value' cambia para mantener la reactividad.
  watch(
    () => props.value,
    (newVal) => {
      dialog.value = newVal
    }
  )

  function closeDialog() {
    dialog.value = false
    emit('update:value', false)
  }

  // Capture Escape at document level BEFORE Vuetify's persistent handler can intercept it
  function handleEscapeCapture(e: KeyboardEvent) {
    if (e.key === 'Escape' && dialog.value) {
      e.stopPropagation()
      e.preventDefault()
      closeDialog()
    }
  }

  watch(dialog, (open) => {
    if (open) {
      document.addEventListener('keydown', handleEscapeCapture, { capture: true })
    } else {
      document.removeEventListener('keydown', handleEscapeCapture, { capture: true })
    }
  })

  function closeModalByClickingOut(e: Event) {
    if (!dialog.value) {
      beforeUnmount()
      return
    }

    const isModalClicked = !!(e.target as HTMLElement).closest('.modal-panel')
    if (isModalClicked) return

    if (props.exitClickOut) dialog.value = false
  }

  function beforeUnmount() {
    document.removeEventListener('keydown', handleEscapeCapture, { capture: true })
    toastStore.clearAllModalToasts()
    messageStore.clearMessage()
  }

  onBeforeUnmount(beforeUnmount)
</script>

<style lang="scss" scoped>
  .modal-component {
    :deep(.v-overlay__content) {
      margin: 0;
      width: $size-full;
      max-width: $size-full;
      max-height: $size-full;
    }

    $close-button-size: $size-32;

    .modal-panel {
      container-type: inline-size;
      width: $size-full;
      height: $size-screen-h;
      display: flex;
      flex-direction: column;
      align-items: center;
      background-color: white;
      border-radius: $b-radius-0;
      overflow: hidden auto;
      padding: $spacing-l;
      margin: auto;

      .header {
        display: flex;
        flex-direction: row;
        max-width: $size-375;
        width: $size-full;
        justify-content: space-between;
        align-items: start;
        margin-bottom: $spacing-s;
        position: relative;
        padding-left: calc($close-button-size / 2);

        .title {
          width: $size-full;
        }

        :deep(.gl_form-title) {
          padding-right: $spacing-48;
          font-size: $font-size-20;
          padding-bottom: $spacing-2xl;

          @include breakpoint-md {
            margin-left: auto;
            margin-right: auto;
            text-align: center;
            padding: 0 0 2rem;
            max-width: $size-390;
          }
        }

        .close-btn {
          position: absolute;
          font-size: $font-size-2xl;
          top: $spacing-s;
          right: 0;
          background: none;
          border: none;
          padding: 0;
          width: auto;
          cursor: pointer;
          line-height: 1;

          @include breakpoint-md {
            position: relative;
            align-self: end;
          }
        }

        @include breakpoint-md {
          max-width: $size-full;
          padding-left: $close-button-size;
          flex-direction: column-reverse;
        }
      }

      .body {
        width: $size-full;
        max-width: $size-390;
        height: $size-full;
        overflow: visible;
      }

      button {
        width: $size-full;
      }
    }

    /* Estilos para la barra de desplazamiento completa */
    ::-webkit-scrollbar {
      width: $size-08;

      /* Ancho de la barra de desplazamiento */
    }

    /* Estilos para el riel de la barra de desplazamiento */
    ::-webkit-scrollbar-track {
      background: $color-neutral-00;

      /* Color del riel */
    }

    /* Estilos para el handle de la barra de desplazamiento */
    ::-webkit-scrollbar-thumb {
      background: $color-grey-500;

      /* Color del handle */
    }

    /* Estilos para el handle de la barra de desplazamiento al pasar el mouse */
    ::-webkit-scrollbar-thumb:hover {
      background: $color-grey-800;

      /* Color del handle al hacer hover */
    }

    /* Estilos para la barra de desplazamiento en Firefox */
    * {
      scrollbar-width: thin;
      scrollbar-color: $color-grey-500 $color-neutral-00;
    }

    &.ConfirmRemoveCoveragesModal .body {
      margin: auto;
    }
  }
</style>
